import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import '@/styles/Board/TiptapTextEditor.less';
import { useEffect, useRef, useState } from 'react';
import { tileButton } from '@/components/Board/commonToolbarStyles';
import { MemoizedEditorCore } from '@/components/Board/TextEditor/InlineTextEditor';
import TiptapFloatingToolbar from '@/components/Board/TextEditor/TiptapFloatingToolbar';
const NoteDetail = ({ note, isCreating = false, isSaving = false, onBack, onCancel, onSave, }) => {
    const [name, setName] = useState('');
    const [text, setText] = useState('');
    const [editorReady, setEditorReady] = useState(false);
    const editorRef = useRef(null);
    const lastNoteIdRef = useRef(null);
    useEffect(() => {
        if (isCreating) {
            setName('');
            setText('');
            lastNoteIdRef.current = null;
        }
        else if (note) {
            const noteId = note.id;
            // Обновляем контент только если заметка изменилась
            if (lastNoteIdRef.current !== noteId) {
                setName(note.name);
                const noteText = note.text || '';
                setText(noteText);
                lastNoteIdRef.current = noteId;
                // Явно устанавливаем контент в редактор после смены заметки
                const timeoutId = setTimeout(() => {
                    const checkAndSetContent = () => {
                        if (editorRef.current) {
                            const editor = editorRef.current.getEditor();
                            if (editor) {
                                // Устанавливаем контент даже если он пустой
                                editorRef.current.setContent(noteText, 'markdown');
                            }
                            else {
                                // Если редактор еще не готов, ждем и пробуем снова
                                requestAnimationFrame(checkAndSetContent);
                            }
                        }
                        else {
                            // Если ref еще не установлен, ждем немного и пробуем снова
                            requestAnimationFrame(checkAndSetContent);
                        }
                    };
                    checkAndSetContent();
                }, 100);
                return () => clearTimeout(timeoutId);
            }
        }
    }, [note, isCreating]);
    // Отслеживание готовности редактора
    useEffect(() => {
        const checkEditorReady = () => {
            var _a;
            const editor = (_a = editorRef.current) === null || _a === void 0 ? void 0 : _a.getEditor();
            if (editor) {
                setEditorReady(true);
            }
            else {
                requestAnimationFrame(checkEditorReady);
            }
        };
        const timeoutId = setTimeout(() => {
            requestAnimationFrame(checkEditorReady);
        }, 100);
        return () => clearTimeout(timeoutId);
    }, [note === null || note === void 0 ? void 0 : note.id, isCreating]);
    const handleSave = () => {
        if (!name.trim() || isSaving)
            return;
        if (onSave) {
            onSave(name.trim(), text || '');
        }
    };
    const isSaveDisabled = !name.trim() || isSaving;
    return (_jsxs("div", { style: {
            display: 'flex',
            flexDirection: 'column',
            gap: 16,
            width: '100%',
            height: '100%',
            pointerEvents: 'auto',
            padding: '4px',
        }, children: [_jsxs("div", { style: {
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 12,
                }, children: [_jsx("input", { type: "text", value: name, onChange: (e) => setName(e.target.value), placeholder: "\u041D\u0430\u0437\u0432\u0430\u043D\u0438\u0435 \u0437\u0430\u043C\u0435\u0442\u043A\u0438", disabled: isSaving, style: {
                            flex: 1,
                            fontSize: 18,
                            fontWeight: 600,
                            color: '#e8eaed',
                            background: 'rgba(255, 255, 255, 0.04)',
                            border: '1px solid rgba(255, 255, 255, 0.1)',
                            borderRadius: 8,
                            padding: '12px 16px',
                            outline: 'none',
                            opacity: isSaving ? 0.6 : 1,
                            cursor: isSaving ? 'not-allowed' : 'text',
                            transition: 'all 0.15s ease',
                            letterSpacing: '-0.01em',
                        }, onFocus: (e) => {
                            e.currentTarget.style.background = 'rgba(255, 255, 255, 0.06)';
                            e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.15)';
                        }, onBlur: (e) => {
                            e.currentTarget.style.background = 'rgba(255, 255, 255, 0.04)';
                            e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.1)';
                        } }), _jsx("div", { style: { display: 'flex', gap: 8, flexShrink: 0 }, children: isCreating ? (_jsxs(_Fragment, { children: [_jsx("button", { type: "button", onClick: onCancel, disabled: isSaving, "aria-label": "\u041E\u0442\u043C\u0435\u043D\u0438\u0442\u044C \u0441\u043E\u0437\u0434\u0430\u043D\u0438\u0435 \u0437\u0430\u043C\u0435\u0442\u043A\u0438", style: Object.assign(Object.assign({}, tileButton(false, {
                                        padding: '10px 16px',
                                        height: 44,
                                        opacity: isSaving ? 0.5 : 1,
                                    })), { background: 'rgba(255, 255, 255, 0.04)', border: '1px solid rgba(255, 255, 255, 0.1)', color: '#9aa0a6', fontSize: 14, fontWeight: 500, transition: 'all 0.15s ease' }), onMouseEnter: (e) => {
                                        if (!isSaving) {
                                            e.currentTarget.style.background = 'rgba(255, 255, 255, 0.06)';
                                            e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.15)';
                                        }
                                    }, onMouseLeave: (e) => {
                                        e.currentTarget.style.background = 'rgba(255, 255, 255, 0.04)';
                                        e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.1)';
                                    }, children: "\u041E\u0442\u043C\u0435\u043D\u0430" }), _jsx("button", { type: "button", onClick: handleSave, disabled: isSaveDisabled, "aria-label": "\u0421\u043E\u0445\u0440\u0430\u043D\u0438\u0442\u044C \u0437\u0430\u043C\u0435\u0442\u043A\u0443", style: Object.assign(Object.assign({}, tileButton(true, {
                                        padding: '10px 20px',
                                        height: 44,
                                        opacity: isSaveDisabled ? 0.5 : 1,
                                    })), { background: isSaveDisabled
                                            ? 'rgba(255, 255, 255, 0.04)'
                                            : 'rgba(66, 133, 244, 0.15)', border: isSaveDisabled
                                            ? '1px solid rgba(255, 255, 255, 0.1)'
                                            : '1px solid rgba(66, 133, 244, 0.3)', color: isSaveDisabled ? '#9aa0a6' : '#8ab4f8', fontSize: 14, fontWeight: 500, transition: 'all 0.15s ease' }), onMouseEnter: (e) => {
                                        if (!isSaveDisabled) {
                                            e.currentTarget.style.background = 'rgba(66, 133, 244, 0.2)';
                                            e.currentTarget.style.borderColor = 'rgba(66, 133, 244, 0.4)';
                                        }
                                    }, onMouseLeave: (e) => {
                                        if (!isSaveDisabled) {
                                            e.currentTarget.style.background = 'rgba(66, 133, 244, 0.15)';
                                            e.currentTarget.style.borderColor = 'rgba(66, 133, 244, 0.3)';
                                        }
                                    }, children: isSaving ? 'Сохранение...' : 'Сохранить' })] })) : (_jsxs(_Fragment, { children: [_jsx("button", { type: "button", onClick: onBack, "aria-label": "\u0412\u0435\u0440\u043D\u0443\u0442\u044C\u0441\u044F \u043A \u0441\u043F\u0438\u0441\u043A\u0443 \u0437\u0430\u043C\u0435\u0442\u043E\u043A", style: Object.assign(Object.assign({}, tileButton(false, { padding: '10px 16px', height: 44 })), { background: 'rgba(255, 255, 255, 0.04)', border: '1px solid rgba(255, 255, 255, 0.1)', color: '#9aa0a6', fontSize: 14, fontWeight: 500, transition: 'all 0.15s ease' }), onMouseEnter: (e) => {
                                        e.currentTarget.style.background = 'rgba(255, 255, 255, 0.06)';
                                        e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.15)';
                                    }, onMouseLeave: (e) => {
                                        e.currentTarget.style.background = 'rgba(255, 255, 255, 0.04)';
                                        e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.1)';
                                    }, children: "\u041D\u0430\u0437\u0430\u0434" }), _jsx("button", { type: "button", onClick: handleSave, disabled: isSaveDisabled, "aria-label": "\u0421\u043E\u0445\u0440\u0430\u043D\u0438\u0442\u044C \u0437\u0430\u043C\u0435\u0442\u043A\u0443", style: Object.assign(Object.assign({}, tileButton(true, {
                                        padding: '10px 20px',
                                        height: 44,
                                        opacity: isSaveDisabled ? 0.5 : 1,
                                    })), { background: isSaveDisabled
                                            ? 'rgba(255, 255, 255, 0.04)'
                                            : 'rgba(66, 133, 244, 0.15)', border: isSaveDisabled
                                            ? '1px solid rgba(255, 255, 255, 0.1)'
                                            : '1px solid rgba(66, 133, 244, 0.3)', color: isSaveDisabled ? '#9aa0a6' : '#8ab4f8', fontSize: 14, fontWeight: 500, transition: 'all 0.15s ease' }), onMouseEnter: (e) => {
                                        if (!isSaveDisabled) {
                                            e.currentTarget.style.background = 'rgba(66, 133, 244, 0.2)';
                                            e.currentTarget.style.borderColor = 'rgba(66, 133, 244, 0.4)';
                                        }
                                    }, onMouseLeave: (e) => {
                                        if (!isSaveDisabled) {
                                            e.currentTarget.style.background = 'rgba(66, 133, 244, 0.15)';
                                            e.currentTarget.style.borderColor = 'rgba(66, 133, 244, 0.3)';
                                        }
                                    }, children: isSaving ? 'Сохранение...' : 'Сохранить' })] })) })] }), _jsx("div", { style: {
                    flex: 1,
                    minHeight: 300,
                    background: 'rgba(255, 255, 255, 0.03)',
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                    borderRadius: 8,
                    padding: '16px',
                    opacity: isSaving ? 0.6 : 1,
                    transition: 'all 0.15s ease',
                    display: 'flex',
                    flexDirection: 'column',
                    position: 'relative',
                    overflow: 'hidden',
                }, children: _jsxs("div", { style: {
                        flex: 1,
                        width: '100%',
                        minHeight: '200px',
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                    }, children: [_jsx(MemoizedEditorCore, { ref: editorRef, content: text || '', onUpdate: setText, editable: !isSaving, placeholder: "\u0422\u0435\u043A\u0441\u0442 \u0437\u0430\u043C\u0435\u0442\u043A\u0438...", style: {
                                flex: 1,
                                width: '100%',
                                minHeight: '200px',
                                height: '100%',
                                color: '#e8eaed',
                                fontSize: 14,
                                lineHeight: 1.6,
                                fontFamily: 'inherit',
                            }, className: "tiptap-text-editor" }, (note === null || note === void 0 ? void 0 : note.id) || (isCreating ? 'new' : 'empty')), editorReady && editorRef.current && (_jsx(TiptapFloatingToolbar, { editor: editorRef.current.getEditor(), zoom: 1, stagePosition: { x: 0, y: 0 } }))] }) })] }));
};
export default NoteDetail;
